import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import AccountsPanel from "./components/AccountsPanel";

interface Payment {
  invoice_code: string;
  nama_lengkap: string;
  nomor_telepon: string;
  bukti_bayar_url: string;
  bukti_bayar_uploaded_at: string;
  payment_lock_reason: string;
}

function App() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"payments" | "accounts">("payments");
  // @ts-ignore
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  // @ts-ignore
  const [showImageModal, setShowImageModal] = useState(false);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/payments/pending`);
      const data = await res.json();
      if (data.success) {
        setPayments(data.pending);
      } else {
        setError(data.error || "Gagal mengambil data");
      }
    } catch (error) {
      setError("Gagal memuat data pembayaran");
    } finally {
      setLoading(false);
    }
  };

  const handleViewImage = (payment: Payment) => {
    if (payment.bukti_bayar_url) {
      setSelectedPayment(payment);
      setShowImageModal(true);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // @ts-ignore
  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedPayment(null);
  };

  const handleUnlock = async (payment: Payment) => {
    const result = await Swal.fire({
      title: "Buka Kunci Pembayaran",
      input: "textarea",
      inputLabel: "Catatan Admin (opsional)",
      inputPlaceholder: "Masukkan catatan untuk unlock...",
      showCancelButton: true,
      confirmButtonText: "Buka Kunci",
      cancelButtonText: "Batal",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      showLoaderOnConfirm: true,
      preConfirm: async (notes) => {
        try {
          const res = await fetch(`/api/payments/unlock`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              invoice_code: payment.invoice_code,
              admin_notes: notes || "",
            }),
          });
          const data = await res.json();
          if (!data.success) {
            throw new Error(data.error || "Gagal membuka kunci");
          }
          return data;
        } catch (error) {
          Swal.showValidationMessage(error instanceof Error ? error.message : "Gagal membuka kunci");
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      await Swal.fire({
        icon: "success",
        title: "Kunci Dibuka",
        text: "Halaman pembayaran dapat dilanjutkan. Bot akan melanjutkan booking.",
        confirmButtonColor: "#28a745",
      });
      fetchPayments();
    }
  };

  useEffect(() => {
    fetchPayments();
    const interval = setInterval(fetchPayments, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <h1 style={{ fontSize: "28px", fontWeight: 700, color: "#333" }}>Aesel Admin Dashboard</h1>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setTab("payments")}
            className="btn"
            style={{
              background: tab === "payments" ? "#b22e15" : "#e9ecef",
              color: tab === "payments" ? "#fff" : "#333",
            }}
          >
            💳 Verifikasi Pembayaran
          </button>
          <button
            onClick={() => setTab("accounts")}
            className="btn"
            style={{
              background: tab === "accounts" ? "#b22e15" : "#e9ecef",
              color: tab === "accounts" ? "#fff" : "#333",
            }}
          >
            📝 Buat Akun Prometric
          </button>
        </div>
      </div>

      {tab === "accounts" ? (
        <AccountsPanel />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "700" }}>Daftar Pembayaran Menunggu Verifikasi ({payments.length})</h3>
            <button onClick={fetchPayments} disabled={loading} className="btn btn-primary">
              🔄 Refresh
            </button>
          </div>

          {error && <div style={{ backgroundColor: "#f8d7da", color: "#721c24", padding: "12px", borderRadius: "8px", marginBottom: "20px" }}>{error}</div>}

          <div className="card">
            {payments.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>Tidak ada pembayaran menunggu verifikasi</div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Nama</th>
                      <th>No Telepon</th>
                      <th>Upload</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.invoice_code}>
                        <td style={{ fontFamily: "monospace", fontSize: "13px" }}>{payment.invoice_code}</td>
                        <td>{payment.nama_lengkap}</td>
                        <td style={{ fontFamily: "monospace", fontSize: "13px" }}>{payment.nomor_telepon}</td>
                        <td>
                          <span style={{ color: "#28a745" }}>✓ Uploaded</span>
                        </td>
                        <td>
                          <span className="badge badge-pending">Menunggu Verifikasi</span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={() => handleViewImage(payment)} className="btn btn-primary" style={{ padding: "6px 12px", fontSize: "12px" }}>
                              👁️ Lihat
                            </button>
                            <button
                              onClick={() =>
                                handleUnlock({
                                  invoice_code: payment.invoice_code,
                                  nama_lengkap: payment.nama_lengkap,
                                  nomor_telepon: payment.nomor_telepon,
                                  bukti_bayar_url: payment.bukti_bayar_url,
                                  bukti_bayar_uploaded_at: payment.bukti_bayar_uploaded_at,
                                  payment_lock_reason: "",
                                })
                              }
                              className="btn btn-success"
                              style={{ padding: "6px 12px", fontSize: "12px" }}>
                              🔓 Unlock
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default App;
